import { memo, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";

import { cn } from "@/utils/css";
import {
    protectedRoutes,
    useIsAnalyticRoute,
    useIsDataRoute,
    useIsFeedbackRoute,
    useIsSearchRoute,
    useLocationPotentialRouteState,
} from "@/routing";
import { CargoMindsLogo } from "@/components/common/svgs";
import { Feedback, NavDropdown } from "@/components/layout";
import { setInputSearch, setSearchType } from "@/state/searchInputSlice";
import { SearchTab } from "@/components/common/buttons";
import { SearchInputs } from "@/components/search";
import { useTheme } from "@/components/layout/useTheme";

export const MainLayout = memo(({ children, searchTopRef }) => {
    const dispatch = useDispatch();
    const { isDarkTheme } = useTheme();
    const isSearchRoute = useIsSearchRoute();
    const isFeedbackRoute = useIsFeedbackRoute();
    const isDataRoute = useIsDataRoute();
    const isAnalyticRoute = useIsAnalyticRoute();

    const location = useLocation();
    const [pageHeight, setPageHeight] = useState(Math.max(document.body.scrollHeight, document.documentElement.scrollHeight));
    const [randomResizer, setRandomResizer] = useState(0);
    const routeSearchType = useLocationPotentialRouteState();

    const { trailerType, pickup, delivery, pickupDate } = useSelector((state) => state.currentSearch);
    const currentSearch = useSelector((state) => state.currentSearch);
    const searchInputs = useSelector((state) => state.searchInput);

    const hasError = useSelector((state) => state.loadRates.ratePrediction.error || state.marketPotential.error);
    const { searchType } = useSelector((state) => state.searchInput);
    const { data: userData } = useSelector((state) => state.profile);

    const handleSet = () => {
        setRandomResizer(Math.random());
    };

    const debouncedResize = useMemo(() => debounce(handleSet, 300), []);

    useEffect(() => {
        setPageHeight(0);
        setTimeout(() => {
            setPageHeight(Math.max(document.body.scrollHeight, document.documentElement.scrollHeight));
        }, 300);
    }, [location.pathname, randomResizer]);

    useEffect(() => {
        window.addEventListener("resize", debouncedResize);

        return () => {
            window.removeEventListener("resize", debouncedResize);
            debouncedResize.cancel();
        };
    }, [debouncedResize]);

    const handleSearchTabClick = (type) => {
        dispatch(setSearchType(type));
        if (isDataRoute && type === routeSearchType && trailerType && pickup && pickupDate) {
            dispatch(
                setInputSearch({
                    trailerType,
                    pickup,
                    delivery,
                    pickupDate,
                }),
            );
        }
    };

    return (
        <div className={cn("flex flex-col w-full items-center", { "overflow-hidden": hasError })} ref={searchTopRef}>
            <div className="flex flex-col w-full items-center justify-between px-6 relative">
                <div className="flex flex-row max-w-[1440px] w-full h-[109px] md:h-[68px] items-start md:items-center justify-between mx-auto relative pt-4 md:pt-4 md:pb-2">
                    <div className="w-[195px] h-[36px] md:h-[27px] pt-[9px] md:pt-0">
                        <Link to={protectedRoutes[0].path}>
                            <CargoMindsLogo dark={isDarkTheme} />
                        </Link>
                    </div>
                    {isSearchRoute ? (
                        <div className="flex items-center justify-center flex-1 absolute top-[60px] md:static w-full">
                            <div className={cn("flex flex-col md:flex-row h-11")}>
                                <SearchTab
                                    text="Market potential"
                                    onClick={() => handleSearchTabClick("market_potential")}
                                    isActive={searchType === "market_potential"}
                                />
                                <SearchTab
                                    isActive={searchType === "loadboard"}
                                    text="Loadboard"
                                    onClick={() => handleSearchTabClick("loadboard")}
                                />
                                <SearchTab
                                    text="Load rates"
                                    onClick={() => handleSearchTabClick("load_rates")}
                                    isActive={searchType === "load_rates"}
                                />
                                {/*<SearchTab*/}
                                {/*    to={protectedRoutes[4].path}*/}
                                {/*    isActive={searchType === "company_analytics"}*/}
                                {/*    text="Company analytics"*/}
                                {/*    onClick={() => handleSearchTabClick("company_analytics")}*/}
                                {/*/>*/}
                            </div>
                        </div>
                    ) : null}
                    {userData ? (
                        <div>
                            <NavDropdown />
                        </div>
                    ) : null}
                </div>
                {isDataRoute || isAnalyticRoute ? (
                    <div
                        className={cn("w-full absolute top-[178px] z-20 transition-all duration-500 ease-in-out", {
                            "opacity-100 visible": routeSearchType !== searchType,
                            "opacity-0 invisible": routeSearchType === searchType,
                        })}
                    >
                        <div
                            className={cn("bg-opacity-60", {
                                "bg-black": isDarkTheme,
                                "bg-[#E8EAE9]": !isDarkTheme,
                            })}
                            style={{ height: `calc(${pageHeight}px - 178px)` }}
                            onClick={() => {
                                dispatch(setSearchType(routeSearchType));
                            }}
                        />
                    </div>
                ) : null}
            </div>
            {isSearchRoute ? (
                <div
                    className={cn("transition-[max-height,height,opacity] duration-300 ease-out w-full max-h-[366px] opacity-100", {
                        "max-h-0 opacity-0": isAnalyticRoute && searchType === "company_analytics",
                    })}
                >
                    <SearchInputs />
                </div>
            ) : null}
            <div className="flex flex-col w-full max-w-[1472px] p-4">{children}</div>
            {isFeedbackRoute ? <Feedback /> : null}
        </div>
    );
});
